import { useEffect } from 'react';
import { Button, Form, Input, Select } from 'antd';
import { UpOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { useSearchParams } from '../../../../hooks/useSearchParams';
import { capitalizeFirstLetter, getValidSearchParams } from '../../../../utils';
import { ticketsListDefaultSearchParams } from '..';
import SelectUser from '../../../Common/SelectUser';
import { SelectTicketStatus } from '../../../Common/SelectTicketStatus';
import { TicketStatus, TicketType } from '../../../../store/ticket';
import { RangePicker } from '../../../Common/RangePicker';

import styles from './index.module.scss';

export interface FilterFormValues {
  ticketId?: string;
  name?: string;
  address?: string;
  categoryAndSubCategoryIds: string[];
  type?: string;
  zipCode?: string;
  email?: string;
  userId?: string | null;
  phone?: string;
  website?: string;
  updatedAt?: [Date, Date] | null;
  status?: TicketStatus | null;
}

interface QueryValues {
  ticketId?: string;
  name?: string;
  address?: string;
  categoryId?: string;
  subCategoryId?: string;
  type?: string;
  zipCode?: string;
  email?: string;
  user?: string;
  phone?: string;
  updatedAtStart?: string;
  updatedAtEnd?: string;
  status?: TicketStatus | null;
}

const defaultFormValues: FilterFormValues = {
  ticketId: '',
  name: '',
  address: '',
  categoryAndSubCategoryIds: [],
  type: '',
  zipCode: '',
  email: '',
  userId: null,
  phone: '',
  website: '',
  updatedAt: null,
  status: null,
};

interface FilterProps {
  onClose: () => void;
}

function Filter({ onClose }: FilterProps) {
  const [form] = Form.useForm<FilterFormValues>();

  const { searchParams, replaceSearchParams } = useSearchParams();

  const onFinish = ({ updatedAt, categoryAndSubCategoryIds, userId, ...values }: FilterFormValues) => {
    replaceSearchParams({
      ...values,
      updatedAtStart: updatedAt ? updatedAt?.[0].toISOString() : '',
      updatedAtEnd: updatedAt ? updatedAt?.[1].toISOString() : '',
      categoryId: categoryAndSubCategoryIds?.[0],
      subCategoryId: categoryAndSubCategoryIds?.[1],
      userId: userId as string,
    });
  };

  useEffect(() => {
    // eslint-disable-next-line max-len
    const { updatedAtStart, updatedAtEnd, categoryId, subCategoryId, user, ...values } = getValidSearchParams<QueryValues>('*', searchParams);

    form.setFieldsValue({
      ...values,
      updatedAt: updatedAtStart && updatedAtEnd ? [dayjs(updatedAtStart), dayjs(updatedAtEnd)] : undefined,
      categoryAndSubCategoryIds: categoryId ? ([categoryId, subCategoryId].filter(Boolean) as string[]) : [],
      userId: user,
    });
  }, [searchParams]);

  const clear = () => {
    form.setFieldsValue({
      ...defaultFormValues,
      updatedAt: defaultFormValues.updatedAt || undefined,
    });
    replaceSearchParams(ticketsListDefaultSearchParams, { replace: true });
  };

  return (
    <Form<FilterFormValues>
      className={styles.form}
      onFinish={onFinish}
      initialValues={getValidSearchParams<FilterFormValues>('*', searchParams)}
      form={form}
    >
      <div className={styles.formRow}>
        <Form.Item label="Ticket No." name="ticketId">
          <Input
            placeholder="Ticket No."
            type="number"
            onKeyDown={(e) => {
              if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                e.preventDefault();
              }
            }}
          />
        </Form.Item>
        <Form.Item label="Ticket Type" name="type">
          <Select
            options={Object.values(TicketType).map((value) => ({
              label: capitalizeFirstLetter(value),
              value,
            }))}
          />
        </Form.Item>
        <SelectUser defaultUserId={searchParams.get('userId') || ''} formItemProps={{ label: 'Reporter' }} />
        <RangePicker form={form} />
        <SelectTicketStatus />
      </div>

      <div className={styles.buttons}>
        <Button htmlType="button" onClick={clear}>
          Clear
        </Button>
        <Button htmlType="submit" type="primary">
          Apply
        </Button>
        <Button htmlType="button" onClick={onClose}>
          Close
          {' '}
          <UpOutlined />
        </Button>
      </div>
    </Form>
  );
}

export default Filter;
